import "./site/error-beacon";
import { Throttle, ThrottleResult, InputSample, LandingEvent, WreckEvent } from "./games/throttle/throttle";
import { recordRun, showXpToast } from "./site/profile";
import { enqueueFailedSubmit, flushFailedSubmits, isRetryable } from "./site/offline-queue";
import "./ui/styles.css";

const PB_KEY = "aether.throttle_pb";
const HANDLE_KEY = "aether.handle";
/** Phase 2: persisted PB ghost — last best run's input stream + score.
 *  Replayed translucently during the next run as a self-pace marker. */
const GHOST_KEY = "aether.throttle_ghost";
interface GhostRecord { score: number; inputs: import("./games/throttle/throttle").InputSample[] }

flushFailedSubmits("throttle");

interface StartResp { run_id: string; seed: number; token: string; game: string }

async function startThrottleRun(): Promise<StartResp | null> {
  try {
    const res = await fetch("/api/runs/throttle", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as StartResp;
  } catch { return null; }
}

async function finishThrottleRun(
  runId: string,
  handle: string,
  inputs: InputSample[],
  landings: LandingEvent[],
  wreck: WreckEvent | null,
) {
  const url = `/api/runs/throttle/${runId}`;
  const body = { handle, inputs, landings, wreck };
  try {
    const res = await fetch(url, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      if (isRetryable(res.status)) enqueueFailedSubmit("throttle", url, body);
      return null;
    }
    return await res.json();
  } catch {
    enqueueFailedSubmit("throttle", url, body);
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudDistance = document.getElementById("hud-distance")!;
const hudTime = document.getElementById("hud-time")!;
const hudClean = document.getElementById("hud-clean")!;
const hudScore = document.getElementById("hud-score")!;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;

let game: Throttle | null = null;
let activeRunId: string | null = null;
let pendingResult: ThrottleResult | null = null;

const RUN_MS = 60_000;

async function startRun() {
  if (game) game.dispose();
  const runStart = await startThrottleRun();
  activeRunId = runStart?.run_id ?? null;
  const seed = runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);

  game = new Throttle(canvas, {
    onHud: (runMs, distance, cleanLandings, scoreEstimate) => {
      hudDistance.textContent = Math.floor(distance).toString();
      hudTime.textContent = ((RUN_MS - runMs) / 1000).toFixed(1);
      hudClean.textContent = cleanLandings.toString();
      hudScore.textContent = scoreEstimate.toLocaleString();
    },
    onGameOver: async (result) => {
      hud.dataset.state = "over";
      msgTitle.textContent = result.wrecked ? "Wrecked" : "Run complete";
      const trickNote = result.trickCount > 0
        ? ` · ${result.trickCount} trick${result.trickCount === 1 ? "" : "s"}` +
          (result.peakTrickCombo > 1 ? ` (peak ${result.peakTrickCombo}×)` : "")
        : "";
      msgBody.textContent = `${result.scoreEstimate.toLocaleString()} pts · ${Math.floor(result.distance_m)}m · ${result.clean_landings} clean${trickNote}`;
      btnStart.textContent = "Try again";

      try {
        const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
        if (result.scoreEstimate > pb) {
          localStorage.setItem(PB_KEY, String(result.scoreEstimate));
          // Phase 2: also save the input stream so the next run gets a
          // ghost. Stored as JSON {score, inputs}. Capped client-side
          // already (MAX_INPUTS = 900), so payload is bounded.
          const rec: GhostRecord = { score: result.scoreEstimate, inputs: result.inputs };
          try { localStorage.setItem(GHOST_KEY, JSON.stringify(rec)); } catch { /* quota */ }
        }
      } catch { /* private */ }
      const xp = recordRun({ game: "throttle", score: result.scoreEstimate });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishThrottleRun(activeRunId, handle, result.inputs, result.landings, result.wreck);
        if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
      } else {
        pendingResult = result;
        submitForm.hidden = false;
      }
    },
  }, seed);
  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* defaults */ }
  // Phase 2: feed the saved PB ghost (if any) before starting the run.
  try {
    const raw = localStorage.getItem(GHOST_KEY);
    if (raw) {
      const ghost = JSON.parse(raw) as GhostRecord;
      if (ghost && Array.isArray(ghost.inputs)) game.setGhostInputs(ghost.inputs);
    }
  } catch { /* ghost off */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Throttle";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  if (!activeRunId || !pendingResult) return;
  const value = handleInputOverlay.value.trim();
  if (!/^[A-Za-z0-9_-]{1,16}$/.test(value)) return;
  localStorage.setItem(HANDLE_KEY, value);
  const res = await finishThrottleRun(activeRunId, value, pendingResult.inputs, pendingResult.landings, pendingResult.wreck);
  pendingResult = null;
  submitForm.hidden = true;
  if (res) msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") startRun();
});
